import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { RewardCardFace } from './RewardCard';

/**
 * "Add to album" (05.10, Figma 13909:538143 → 14033:39816 → 14033:40232): the
 * card the daily pick turned up flies out of the dialog into its slot in My
 * Collection, and a "New" plate drops onto it once it has landed.
 *
 * The pick dialog and the collection panel are siblings on the hub page, so
 * they meet here:
 * - `held`: cards the open dialog has revealed. The panel keeps them out of
 *   their slot and out of the album count until they arrive — the count ticks
 *   over with the flight, not behind the dialog's backdrop.
 * - `flight`: the card in the air, with where it took off from.
 * - `landed`: cards that arrived this visit; they wear "New". Kept in
 *   sessionStorage, so a card picked in the trade zone still wears it on the
 *   hub after the full page load between zones.
 */

/** Timings and curves straight off the 05.10 motion table. */
export const ALBUM_FLIGHT_MS = 800;
export const ALBUM_FLIGHT_EASE = [0.42, 0, 0.58, 1] as const;

export interface AlbumFlight {
  cardId: string;
  face: RewardCardFace;
  /** The card's box in the dialog, viewport coordinates. */
  from: { left: number; top: number; width: number };
  /** Flies to the Rewards Hub link in the header instead of an album slot (global daily card). */
  to?: 'rewards-hub';
}

interface AlbumFlightState {
  held: string[];
  flight: AlbumFlight | null;
  landed: string[];
  /** The dialog has revealed these; keep them out of the album for now. */
  hold: (cardIds: string[]) => void;
  /** Take off. The count updates now; the slot fills on landing. */
  launch: (flight: AlbumFlight) => void;
  /** Touched down (or there was nowhere to fly): the card is in, wearing "New". */
  land: (cardIds: string[]) => void;
}

function withAll(list: string[], ids: string[]): string[] {
  const missing = ids.filter((id) => !list.includes(id));
  return missing.length ? [...list, ...missing] : list;
}

/** A flight nobody lands — no album panel on the page — is put down after this. */
const LANDING_FALLBACK_MS = ALBUM_FLIGHT_MS + 400;

export const useAlbumFlightStore = create<AlbumFlightState>()(
  persist(
    (set, get) => ({
      held: [],
      flight: null,
      landed: [],
      hold: (cardIds) => set((state) => ({ held: withAll(state.held, cardIds) })),
      launch: (flight) => {
        set((state) => ({ flight, held: state.held.filter((id) => id !== flight.cardId) }));
        setTimeout(() => get().land([flight.cardId]), LANDING_FALLBACK_MS);
      },
      land: (cardIds) =>
        set((state) => ({
          held: state.held.filter((id) => !cardIds.includes(id)),
          flight: state.flight && cardIds.includes(state.flight.cardId) ? null : state.flight,
          landed: withAll(state.landed, cardIds),
        })),
    }),
    {
      name: 'album-flight',
      storage: createJSONStorage(() => sessionStorage),
      partialize: ({ landed }) => ({ landed }),
      // The hub is drawn on the server without "New": rehydrated after mount (GlobalDailyCard).
      skipHydration: true,
    },
  ),
);
